/**
 * React Query hook for L3 sales-point contacts.
 * Kept outside {@link ./salesPointContactsApi} so session bootstrap modules
 * (CSP / oidc land) can import claim helpers without a circular graph into
 * CustomerSessionProvider — Vite Fast Refresh otherwise full-reloads CSP.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { ACCOUNT_ME_STALE_MS } from '../../shared/session/fetchPlatformMe.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import {
  fetchSalesPointContacts,
} from './salesPointContactsApi.js';
import type { SalesPointContactsListDTO } from './salesPointContactsTypes.js';

const CONTACTS_QUERY_OPTIONS = {
  staleTime: ACCOUNT_ME_STALE_MS,
  refetchOnMount: false as const,
  refetchOnWindowFocus: false,
};

/**
 * GET contacts via L3 absolute `/api/v1/...` + credentials include.
 * G5 Spec A: enablement matches global chrome — `/me`-established session
 * memory only ({@link isGloballySignedInChrome}). BAN jwt LS alone (jwt-stale
 * must not fire auth'd L3). BAN FE mode triad / getToken-as-signed-in.
 */
export function useSalesPointContactsByToken(
  enabled = true,
): UseQueryResult<SalesPointContactsListDTO, Error> {
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const signedInChrome = isGloballySignedInChrome({ session });
  const customerKey =
    session?.customerId ?? (signedInChrome ? 'session' : -1);

  return useQuery({
    queryKey: customerQueryKeys.salesPointContacts(customerKey),
    queryFn: ({ signal }) =>
      fetchSalesPointContacts({
        apiBaseUrl,
        signal,
      }),
    enabled: enabled && signedInChrome,
    ...CONTACTS_QUERY_OPTIONS,
  });
}
